/**
 * Add Positions To Portfolio Dialog
 * 
 * Extended version of the AddToPortfolioDialog that can handle multiple positions
 * Used for the "Build Portfolio" button functionality
 */

import React from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
// Using modular dialog shell instead of raw Dialog primitives
import { AddPositionsDialogShell } from "./AddPositionsDialogShell";

import { Plug, Package } from "lucide-react";
import { WealthicaPosition } from "@/providers/wealthica2/types";
import PortfolioSelector from "@/components/funds-raw/portfolio/PortfolioSelector";
import PortfolioCreator from "@/components/funds-raw/portfolio/PortfolioCreator";
import { AddPositionsList } from "./AddPositionsList";
import { useAddPositionsDialog } from "./useAddPositionsDialog";
import type { GenericPosition } from "./useAddPositionsDialog";


interface AddPositionsToPortfolioDialogProps {
  isOpen?: boolean;
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  positions: (WealthicaPosition | GenericPosition)[];
  onSuccess?: () => void;
  // Optional UX: navigate to build page even when zero new positions were added to an existing portfolio
  navigateOnZeroAdded?: boolean;
}

export default function AddPositionsToPortfolioDialog({
  isOpen,
  open,
  onClose,
  onOpenChange,
  positions,
  onSuccess,
  navigateOnZeroAdded,
}: AddPositionsToPortfolioDialogProps) {
  const navigate = useNavigate();

  const dlg = useAddPositionsDialog({
    isOpen,
    open,
    onClose,
    onOpenChange,
    positions,
    onSuccess,
    navigateOnZeroAdded,
  });

  // Don't render anything if dialog is not open to avoid mounting issues
  if (!dlg.isDialogOpen) return null;

  const anyProcessing = Object.values(dlg.processingPositions || {}).some(Boolean);
  // Fix validation: portfolio type should be valid for personal/client, empty string is treated as personal
  const hasValidPortfolioType = dlg.portfolioType === 'personal' || dlg.portfolioType === 'client' || dlg.portfolioType === '';
  const primaryDisabled = dlg.isLoading || (dlg.isCreatingNew ? (!dlg.newPortfolioName || !hasValidPortfolioType) : (!dlg.selectedPortfolioId || dlg.selectedIsModel)) || anyProcessing;

  return (
    <AddPositionsDialogShell
      open={dlg.isDialogOpen}
      onOpenChange={dlg.handleOpenChange}
      title="Build Portfolio"
      description={dlg.hasPositions ? `Add ${positions.length} holding${positions.length !== 1 ? 's' : ''} and assets to a portfolio.` : 'No investment holdings found to build a portfolio.'}
      errorMessage={dlg.errorMessage || undefined}
      isProcessing={dlg.isLoading || anyProcessing}
      onCancel={dlg.handleClose}
      onPrimary={dlg.handleAddPositions}
      primaryDisabled={primaryDisabled}
    >
      {dlg.hasPositions ? (
        <>
          <AddPositionsList
            selectedPositions={dlg.selectedPositions}
            showAllPositions={dlg.showAllPositions}
            setShowAllPositions={dlg.setShowAllPositions}
            onRemove={dlg.removePosition}
          />

          <div className="grid gap-4 py-4">
            {!dlg.isCreatingNew ? (
              <PortfolioSelector
                portfolios={dlg.portfolios || []}
                selectedPortfolioId={dlg.safeSelectedPortfolioId}
                setSelectedPortfolioId={dlg.setSelectedPortfolioId}
                onCreateNew={() => {
                  dlg.setPortfolioType('personal');
                  dlg.setIsCreatingNew(true);
                }}
                isLoading={dlg.isLoading}
                isComboOpen={dlg.isComboOpen}
                setIsComboOpen={dlg.setIsComboOpen}
              />
            ) : (
              <PortfolioCreator
                newPortfolioName={dlg.newPortfolioName}
                setNewPortfolioName={dlg.setNewPortfolioName}
                portfolioType={dlg.portfolioType}
                setPortfolioType={dlg.setPortfolioType}
                onBack={() => dlg.setIsCreatingNew(false)}
                isLoading={dlg.isLoading}
                isTypeOpen={dlg.isTypeOpen}
                setIsTypeOpen={dlg.setIsTypeOpen}
                hideModelType={true}
              />
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Assets Available</h3>
          <p className="text-slate-500 mb-4 max-w-sm">No investments found to build a portfolio.</p>
          <p className="text-slate-500 mb-4 max-w-sm">Connect your investment accounts or add assets manually to start building portfolios with your holdings.</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="gap-1"
              onClick={() => {
                dlg.handleOpenChange(false);
                setTimeout(() => navigate('/investments/connect'), 100);
              }}
            >
              <Plug className="h-4 w-4" />
              Connect Accounts
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => {
                dlg.handleOpenChange(false);
                setTimeout(() => navigate('/assets'), 100);
              }}
            >
              <Package className="h-4 w-4" />
              Add Assets
            </Button>
          </div>
        </div>
      )}

      {(dlg.successCount > 0 || dlg.failedCount > 0) && (
        <div className="my-2 p-2 bg-slate-50 rounded-md">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm text-slate-500">{dlg.successCount + dlg.failedCount}/{dlg.selectedPositions?.length || 0}</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${((dlg.successCount + dlg.failedCount) / (dlg.selectedPositions?.length || 1)) * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs">
            <span className="text-green-600">{dlg.successCount} added</span>
            {dlg.failedCount > 0 && <span className="text-destructive">{dlg.failedCount} failed</span>}
          </div>
        </div>
      )}
    </AddPositionsDialogShell>
  );
}
